import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { UserPlus, Search, KeyRound, Trash2, Pencil, Mail, Users, Upload, Send, Eye, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { UserImportDialog } from "@/components/UserImportDialog";
import { MoveInKitPreviewDialog } from "@/components/MoveInKitPreviewDialog";
import { FeatureGrantsDialog } from "@/components/FeatureGrantsDialog";
import { AdminPasswordDialog } from "@/components/AdminPasswordDialog";
import { BulkPasswordDialog } from "@/components/BulkPasswordDialog";

const ROLES = [
  { value: "admin", label: "Admin", tone: "border-orange-500/40 text-orange-300",
    desc: "Full portal access. Only other admins can create.", admin_only: true },
  { value: "property_manager", label: "Property Manager", tone: "border-amber-500/40 text-amber-300",
    desc: "Community-scoped operator. Only admins can create.", admin_only: true },
  { value: "resident", label: "Resident", tone: "border-emerald-500/40 text-emerald-300",
    desc: "Owner or tenant of a unit." },
  { value: "vendor", label: "Vendor", tone: "border-indigo-500/40 text-indigo-300",
    desc: "External contractor — sees vendor portal only." },
  { value: "maintenance", label: "Maintenance / Staff", tone: "border-sky-500/40 text-sky-300",
    desc: "On-site tech. Sees maintenance tickets + preventive schedule only." },
  { value: "board", label: "Board / Director", tone: "border-violet-500/40 text-violet-300",
    desc: "Community board member. Sees board portal + finance." },
  { value: "owner", label: "Owner", tone: "border-rose-500/40 text-rose-300",
    desc: "Investor / non-resident owner. Sees owner reports." },
];

const empty = { name: "", email: "", role: "resident", property_ids: [], unit: "", phone: "", send_invite: true, auto_send_movein_kit: false, movein_kit_category: "starter" };

const MOVEIN_KIT_CATEGORIES = [
  { value: "starter", label: "Starter (default)" },
  { value: "house-rules", label: "House rules" },
  { value: "amenities", label: "Amenities" },
];

export default function UsersPage() {
  const { properties } = useProperty();
  const { user: me } = useAuth();   // R116 — sync-available, no null flash
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [roleFilter, setRoleFilter] = useState("all");
  const [propFilter, setPropFilter] = useState("all");
  const [q, setQ] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());  // R112 bulk selection
  const [bulkPwOpen, setBulkPwOpen] = useState(false);
  const [moveinPreviewUser, setMoveinPreviewUser] = useState(null);
  const [permissionsUser, setPermissionsUser] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { include_inactive: includeInactive };
      if (roleFilter !== "all") params.role = roleFilter;
      if (propFilter !== "all") params.property_id = propFilter;
      if (q.trim()) params.query = q.trim();
      const { data } = await api.get("/admin/users", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to load users");
    } finally { setLoading(false); }
  }, [roleFilter, propFilter, q, includeInactive]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(empty); setDialogOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      name: u.name || "", email: u.email || "", role: u.role || "resident",
      property_ids: u.property_ids || [], unit: u.unit || "", phone: u.phone || "",
      send_invite: true, auto_send_movein_kit: false, movein_kit_category: "starter",
    });
    setDialogOpen(true);
  };

  const toggleProp = (pid) => setForm((f) => ({
    ...f,
    property_ids: f.property_ids.includes(pid)
      ? f.property_ids.filter((x) => x !== pid)
      : [...f.property_ids, pid],
  }));

  const submit = async () => {
    setBusy(true);
    try {
      if (editing) {
        const { send_invite, auto_send_movein_kit, movein_kit_category, ...upd } = form;  // patch skips create-only flags
        await api.patch(`/admin/users/${editing.id}`, upd);
        toast.success("User updated");
      } else {
        const payload = { ...form };
        if (!payload.auto_send_movein_kit) delete payload.movein_kit_category;
        const { data } = await api.post("/admin/users", payload);
        const kitTail = data?.movein_kit_sent ? ` · move-in kit delivered (${form.movein_kit_category})` : "";
        // R111 — surface the real invite delivery status rather than
        // pretending everything sent when the email transport silently
        // failed. Common cause: EMERGENT_EMAIL_KEY misconfigured in prod.
        if (form.send_invite) {
          const inv = data?.invite || {};
          if (inv.delivered) {
            toast.success(`Invite emailed to ${form.email}${kitTail}`);
          } else {
            const link = inv.reset_link;
            toast.warning(`Couldn't email the invite`, {
              duration: 10000,
              description: link
                ? `${inv.error || "Email service unavailable"} — the account was created. Copy the setup link below and share it directly.`
                : (inv.error || "Fix the email service and click 'Resend invite' from the user row."),
              action: link ? {
                label: "Copy setup link",
                onClick: () => { navigator.clipboard.writeText(link); toast.info("Link copied to clipboard"); },
              } : undefined,
            });
          }
        } else {
          toast.success(`User created${kitTail}`);
        }
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Save failed");
    } finally { setBusy(false); }
  };

  const sendReset = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      // R111 — real delivery status. If failed, offer to copy the link.
      if (data?.delivered) {
        toast.success(`Password reset email sent to ${u.email}`);
      } else {
        const link = data?.reset_link;
        toast.warning(`Couldn't send the reset email`, {
          duration: 10000,
          description: link
            ? `${data?.error || "Email service unavailable"} — copy the reset link below and share it directly.`
            : (data?.error || "Contact support to check the email service."),
          action: link ? {
            label: "Copy reset link",
            onClick: () => { navigator.clipboard.writeText(link); toast.info("Link copied"); },
          } : undefined,
        });
      }
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to send"); }
  };

  const sendMoveInKit = async (u) => {
    if (!window.confirm(`Send move-in kit email to ${u.email}? It includes wiki highlights, top perks, and how-tos.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/send-movein-kit`);
      toast.success(`Move-in kit sent · ${data.wiki_pages} pages + ${data.perks} perks`, {
        duration: 4500,
        id: `movein-kit-toast-${u.id}`,
        description: `Delivered to ${u.email}`,
      });
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to send"); }
  };

  const deactivate = async (u) => {
    if (!window.confirm(`Deactivate ${u.name}? They won't be able to sign in until reactivated.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Deactivated");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Deactivate failed"); }
  };

  const activate = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { active: true });
      toast.success("Activated");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Activate failed"); }
  };

  const totals = useMemo(() => {
    const t = { admin: 0, resident: 0, vendor: 0, board: 0 };
    items.forEach((u) => { t[u.role] = (t[u.role] || 0) + 1; });
    return t;
  }, [items]);

  return (
    <div>
      <PageHeader eyebrow="Access" title="Users" subtitle="Everyone who signs into the platform — admins, residents, vendors, and board members." />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        {ROLES.map((r) => (
          <Card key={r.value} className="p-3 border-white/10 bg-card" data-testid={`user-count-${r.value}`}>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">{r.label}s</div>
            <div className="text-xl font-semibold tabular-nums text-white mt-0.5">{totals[r.value] || 0}</div>
          </Card>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Button onClick={openCreate} className="bg-orange-500 hover:bg-orange-600 rounded-full" data-testid="user-add-btn">
          <UserPlus className="h-4 w-4 mr-1" /> Add user
        </Button>
        <Button onClick={() => setImportOpen(true)} variant="outline"
                className="border-white/10 rounded-full" data-testid="user-import-btn">
          <Upload className="h-4 w-4 mr-1" /> Bulk import
        </Button>
        {selectedIds.size > 0 && (
          <>
            <div className="h-6 w-px bg-white/10 mx-1" />
            <div className="text-xs text-zinc-400" data-testid="bulk-selection-count">
              {selectedIds.size} selected
            </div>
            <Button size="sm" onClick={() => setBulkPwOpen(true)}
                    className="bg-indigo-500 hover:bg-indigo-600 rounded-full h-8"
                    data-testid="bulk-pw-btn">
              <KeyRound className="h-3.5 w-3.5 mr-1" /> Bulk password
            </Button>
            <Button size="sm" variant="outline" onClick={() => setSelectedIds(new Set())}
                    className="border-white/10 rounded-full h-8"
                    data-testid="bulk-clear-selection">
              Clear
            </Button>
          </>
        )}
        <div className="relative">
          <Search className="h-3 w-3 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search by name or email"
                 className="pl-7 h-9 w-52 bg-white/[0.04] border-white/10" data-testid="user-search" />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-32 h-9 bg-white/[0.04] border-white/10" data-testid="user-role-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
            <SelectItem value="all">All roles</SelectItem>
            {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={propFilter} onValueChange={setPropFilter}>
          <SelectTrigger className="w-48 h-9 bg-white/[0.04] border-white/10" data-testid="user-property-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
            <SelectItem value="all">All communities</SelectItem>
            {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer ml-auto">
          <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)}
                 className="accent-orange-500" data-testid="user-include-inactive" />
          Include deactivated
        </label>
      </div>

      <Card className="border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.03] text-xs">
            <tr>
              <th className="px-3 py-2 w-8">
                <input
                  type="checkbox"
                  className="accent-orange-500 cursor-pointer"
                  data-testid="bulk-select-all"
                  checked={items.length > 0 && items.every((u) => selectedIds.has(u.id))}
                  ref={(el) => {
                    if (el) el.indeterminate = selectedIds.size > 0 && !items.every((u) => selectedIds.has(u.id));
                  }}
                  onChange={(e) => {
                    if (e.target.checked) setSelectedIds(new Set(items.map((u) => u.id)));
                    else setSelectedIds(new Set());
                  }}
                />
              </th>
              <th className="text-left px-3 py-2 text-zinc-400">User</th>
              <th className="text-left px-3 py-2 text-zinc-400">Role</th>
              <th className="text-left px-3 py-2 text-zinc-400">Communities</th>
              <th className="text-left px-3 py-2 text-zinc-400">Status</th>
              <th className="text-right px-3 py-2 text-zinc-400">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => {
              const role = ROLES.find((r) => r.value === u.role) || ROLES[1];
              const active = u.active !== false;
              const selected = selectedIds.has(u.id);
              return (
                <tr key={u.id} className={"border-t border-white/5 " + (selected ? "bg-indigo-500/[0.04]" : "")}
                     data-testid={`user-row-${u.id}`}>
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      className="accent-orange-500 cursor-pointer"
                      checked={selected}
                      onChange={(e) => {
                        const next = new Set(selectedIds);
                        if (e.target.checked) next.add(u.id);
                        else next.delete(u.id);
                        setSelectedIds(next);
                      }}
                      data-testid={`user-select-${u.id}`}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-zinc-100 font-medium">{u.name}</div>
                    <div className="text-[11px] text-zinc-500">{u.email} {u.unit ? `· Unit ${u.unit}` : ""}</div>
                  </td>
                  <td className="px-3 py-2"><Badge variant="outline" className={role.tone}>{role.label}</Badge></td>
                  <td className="px-3 py-2 text-zinc-400 text-xs">
                    {(u.property_ids || []).length > 0
                      ? (u.property_ids || []).map((pid) => properties.find((p) => p.id === pid)?.name || pid).join(", ")
                      : <span className="text-zinc-600">All</span>}
                  </td>
                  <td className="px-3 py-2">
                    {active
                      ? <Badge variant="outline" className={u.email_verified ? "border-emerald-500/40 text-emerald-300" : "border-amber-500/40 text-amber-300"}>
                          {u.email_verified ? "Verified" : "Invited"}
                        </Badge>
                      : <Badge variant="outline" className="border-zinc-600 text-zinc-500">Deactivated</Badge>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => openEdit(u)} title="Edit" className="text-zinc-400 hover:text-orange-300 p-1" data-testid={`user-edit-${u.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => setPasswordUser(u)} title="Change password" className="text-zinc-400 hover:text-indigo-300 p-1" data-testid={`user-reset-${u.id}`}>
                        <KeyRound className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => setPermissionsUser(u)} title="Feature access & permissions"
                              className="text-zinc-400 hover:text-emerald-300 p-1"
                              data-testid={`user-permissions-${u.id}`}>
                        <ShieldCheck className="h-3.5 w-3.5" />
                      </button>
                      {u.role === "resident" && (
                        <>
                          <button onClick={() => setMoveinPreviewUser(u)} title="Preview move-in kit" className="text-zinc-400 hover:text-orange-300 p-1" data-testid={`user-movein-preview-${u.id}`}>
                            <Eye className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => sendMoveInKit(u)} title="Send move-in kit" className="text-zinc-400 hover:text-emerald-300 p-1" data-testid={`user-movein-${u.id}`}>
                            <Send className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                      {active ? (
                        <button onClick={() => deactivate(u)} title="Deactivate" className="text-zinc-400 hover:text-red-300 p-1" data-testid={`user-deactivate-${u.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      ) : (
                        <button onClick={() => activate(u)} title="Reactivate" className="text-zinc-400 hover:text-emerald-300 p-1" data-testid={`user-activate-${u.id}`}>
                          <UserPlus className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && !loading && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-xs text-zinc-500">
                <Users className="h-4 w-4 mx-auto mb-1 opacity-50" />
                No users match those filters.
              </td></tr>
            )}
          </tbody>
        </table>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0a0a0b] border-white/10 text-white sm:max-w-[520px]" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-4 w-4 text-orange-400" /> {editing ? "Edit user" : "Add user"}
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">
              {editing ? "Update the details or reassign communities." :
                "They'll receive an email invite to set their password (unless you turn that off)."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-zinc-400">Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                       placeholder="Full name" className="bg-white/[0.04] border-white/10" data-testid="user-name" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                       disabled={!!editing} placeholder="user@example.com" className="bg-white/[0.04] border-white/10" data-testid="user-email" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-zinc-400">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="bg-white/[0.04] border-white/10" data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                    {ROLES.filter((r) => !(r.admin_only && me?.role !== "admin"))
                          .map((r) => (
                      <SelectItem key={r.value} value={r.value} data-testid={`user-role-option-${r.value}`}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(() => {
                  const r = ROLES.find((x) => x.value === form.role);
                  return r?.desc ? (
                    <div className="text-[10px] text-zinc-500 mt-1 leading-relaxed" data-testid="user-role-desc">
                      {r.desc}
                    </div>
                  ) : null;
                })()}
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Unit (optional)</Label>
                <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}
                       placeholder="12B" className="bg-white/[0.04] border-white/10" data-testid="user-unit" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Phone (optional)</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                     placeholder="+1 555 123 4567" className="bg-white/[0.04] border-white/10" data-testid="user-phone" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Communities</Label>
              <div className="max-h-40 overflow-y-auto rounded-md border border-white/10 p-2 space-y-1">
                {properties.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
                    <input type="checkbox" checked={form.property_ids.includes(p.id)}
                           onChange={() => toggleProp(p.id)} className="accent-orange-500"
                           data-testid={`user-prop-${p.id}`} />
                    {p.name}
                  </label>
                ))}
                {properties.length === 0 && <div className="text-[11px] text-zinc-500">Create a community first.</div>}
              </div>
              <div className="text-[11px] text-zinc-500 mt-1">Empty = portfolio-wide (admins only).</div>
            </div>
            {!editing && (
              <div className="flex items-center justify-between rounded-md border border-white/10 px-3 py-2">
                <div>
                  <Label className="text-zinc-200 text-xs cursor-pointer">Email invite to set password</Label>
                  <div className="text-[11px] text-zinc-500">Uncheck if you&apos;ll share credentials another way.</div>
                </div>
                <Switch checked={form.send_invite} onCheckedChange={(v) => setForm({ ...form, send_invite: v })}
                        data-testid="user-send-invite" />
              </div>
            )}
            {!editing && form.role === "resident" && (
              <div className="rounded-md border border-emerald-500/20 bg-emerald-500/[0.03] px-3 py-2 space-y-2"
                   data-testid="user-auto-movein-row">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-zinc-200 text-xs cursor-pointer">Also send the Move-in Kit</Label>
                    <div className="text-[11px] text-zinc-500">Emails the welcome kit (top wiki pages + perks) the moment the account is created.</div>
                  </div>
                  <Switch checked={form.auto_send_movein_kit}
                          onCheckedChange={(v) => setForm({ ...form, auto_send_movein_kit: v })}
                          data-testid="user-auto-movein-kit" />
                </div>
                {form.auto_send_movein_kit && (
                  <div className="flex items-center gap-2 pt-1 border-t border-emerald-500/15">
                    <Label className="text-[11px] text-zinc-400 shrink-0">Headline category</Label>
                    <Select value={form.movein_kit_category}
                            onValueChange={(v) => setForm({ ...form, movein_kit_category: v })}>
                      <SelectTrigger className="bg-white/[0.04] border-white/10 h-8"
                                     data-testid="user-movein-kit-category">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                        {MOVEIN_KIT_CATEGORIES.map((c) => (
                          <SelectItem key={c.value} value={c.value}
                                       data-testid={`user-movein-kit-cat-${c.value}`}>
                            {c.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={busy} className="border-white/10">Cancel</Button>
            <Button onClick={submit} disabled={busy || !form.name || !form.email} className="bg-orange-500 hover:bg-orange-600" data-testid="user-save-btn">
              {busy ? "Saving…" : editing ? "Save" : "Create + email invite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UserImportDialog open={importOpen} onOpenChange={setImportOpen} onImported={load} />

      <MoveInKitPreviewDialog
        open={!!moveinPreviewUser}
        onOpenChange={(v) => !v && setMoveinPreviewUser(null)}
        user={moveinPreviewUser}
        onSent={load}
      />

      {permissionsUser && (
        <FeatureGrantsDialog
          user={permissionsUser}
          onClose={() => setPermissionsUser(null)}
          onSaved={load}
        />
      )}

      {passwordUser && (
        <AdminPasswordDialog
          user={passwordUser}
          onClose={() => { setPasswordUser(null); }}
        />
      )}

      <BulkPasswordDialog
        open={bulkPwOpen}
        onClose={(reload) => { setBulkPwOpen(false); if (reload) { setSelectedIds(new Set()); load(); } }}
        selectedUsers={items.filter((u) => selectedIds.has(u.id))}
      />
    </div>
  );
}
